import { uniqueId } from 'lodash';
import { GiftedChat } from 'react-native-gifted-chat';
import apis from '@api/api';
import { COMPLETE_ONBOARDING } from '@reduxLocal/actions/types';

export interface ModelHandlersConfig {
  dispatch: any;
  setChatMessages: React.Dispatch<React.SetStateAction<any[]>>;
  setOnboardingStage: (stage: 'profile' | 'context' | 'models' | null) => void;
  fetchIntegrationSuggestions: () => Promise<void>;
  globalUser: any;
  userData: any;
}

/**
 * Handle model selection and creation
 */
export const handleModelSelection = async (
  selectedModels: any[],
  config: ModelHandlersConfig
) => {
  const {
    dispatch,
    setChatMessages,
    setOnboardingStage,
    fetchIntegrationSuggestions,
    globalUser,
    userData,
  } = config;

  if (!selectedModels || selectedModels.length === 0) {
    // If no models selected, skip to integrations
    await handleModelSelectionSkip(config);
    return;
  }

  try {
    console.log('📦 [EasyModeScreen] Creating selected models:', selectedModels.length);

    // Show loading message
    const loadingMessageId = uniqueId();
    setChatMessages((prevMessages) =>
      GiftedChat.append(prevMessages, [
        {
          _id: loadingMessageId,
          text: `Creating ${selectedModels.length} model${selectedModels.length > 1 ? 's' : ''}...`,
          createdAt: new Date(),
          system: true,
          received: true,
        },
      ])
    );

    const userId = globalUser?._id || globalUser?.id;
    const companyId = userData?.companyId || userData?.company?._id;

    // Create each selected model with retry logic
    const createdModels = [];
    const errors = [];
    const maxRetries = 2;

    for (const model of selectedModels) {
      let retryCount = 0;
      let modelCreated = false;

      while (retryCount <= maxRetries && !modelCreated) {
        try {
          // Prepare model data for saving
          const modelData = {
            dataTitle: model.schemaTitle || model.title || 'Model',
            dataTags: model.dataTags || [],
            dataSchema: model.schemaContent || model.schema || [],
            description: model.description || '',
            dataContent: null,
          };

          console.log(`💾 [EasyModeScreen] Saving model: ${model.schemaTitle || model.title} (attempt ${retryCount + 1}/${maxRetries + 1})`);

          // Save the model using the saveData API
          const saveResult = await apis.saveData({
            id: userId,
            data: modelData,
          });

          if (saveResult?.success) {
            createdModels.push(model);
            modelCreated = true;
            console.log('✅ [EasyModeScreen] Model created:', model.schemaTitle || model.title);
          } else {
            const errorMsg = saveResult?.message || saveResult?.error || 'Failed to create model';
            
            if (retryCount < maxRetries) {
              retryCount++;
              console.warn(`⚠️ [EasyModeScreen] Model creation failed, retrying... (${retryCount}/${maxRetries}):`, errorMsg);
              // Wait before retry (exponential backoff)
              await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
              continue;
            } else {
              errors.push({
                model: model.schemaTitle || model.title,
                error: errorMsg,
              });
              console.error('❌ [EasyModeScreen] Model creation failed after retries:', model.schemaTitle || model.title, errorMsg);
            }
          }
        } catch (error: any) {
          const errorMsg = error?.message || error?.response?.data?.message || 'Unknown error';
          
          if (retryCount < maxRetries) {
            retryCount++;
            console.warn(`⚠️ [EasyModeScreen] Error creating model, retrying... (${retryCount}/${maxRetries}):`, errorMsg);
            // Wait before retry (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
            continue;
          } else {
            console.error('❌ [EasyModeScreen] Error creating model after retries:', model.schemaTitle || model.title, errorMsg);
            errors.push({
              model: model.schemaTitle || model.title,
              error: errorMsg,
            });
          }
        }
      }
    }

    // Remove loading message
    setChatMessages((prevMessages) =>
      prevMessages.filter((msg) => msg._id !== loadingMessageId)
    );

    // Show success/error message with retry option if there were errors
    if (createdModels.length > 0) {
      const successMessageId = uniqueId();
      let successText = '';
      
      if (errors.length > 0) {
        successText = `Successfully created ${createdModels.length} of ${selectedModels.length} model${selectedModels.length > 1 ? 's' : ''}. ${errors.length} model${errors.length > 1 ? 's' : ''} failed.`;
        
        // Add retry option for failed models
        const failedModelNames = errors.map(e => e.model).join(', ');
        successText += `\n\nFailed models: ${failedModelNames}`;
      } else {
        successText = `Successfully created ${createdModels.length} model${createdModels.length > 1 ? 's' : ''}!`;
      }

      setChatMessages((prevMessages) =>
        GiftedChat.append(prevMessages, [
          {
            _id: successMessageId,
            text: successText,
            createdAt: new Date(),
            system: true,
            received: true,
            hasErrors: errors.length > 0,
            failedModels: errors,
            canRetry: errors.length > 0,
            retryFunction: errors.length > 0 ? async () => {
              // Retry creating failed models
              const failedModels = selectedModels.filter(model => 
                errors.some(e => e.model === (model.schemaTitle || model.title))
              );
              
              if (failedModels.length > 0) {
                const retryMessageId = uniqueId();
                setChatMessages((prevMessages) =>
                  GiftedChat.append(prevMessages, [
                    {
                      _id: retryMessageId,
                      text: `Retrying creation of ${failedModels.length} model${failedModels.length > 1 ? 's' : ''}...`,
                      createdAt: new Date(),
                      system: true,
                      received: true,
                    },
                  ])
                );
                
                // Retry creating failed models
                const retryCreatedModels = [];
                const retryErrors = [];
                
                for (const model of failedModels) {
                  try {
                    const modelData = {
                      dataTitle: model.schemaTitle || model.title || 'Model',
                      dataTags: model.dataTags || [],
                      dataSchema: model.schemaContent || model.schema || [],
                      description: model.description || '',
                      dataContent: null,
                    };
                    
                    const saveResult = await apis.saveData({
                      id: userId,
                      data: modelData,
                    });
                    
                    if (saveResult?.success) {
                      retryCreatedModels.push(model);
                    } else {
                      retryErrors.push({
                        model: model.schemaTitle || model.title,
                        error: saveResult?.message || 'Failed to create model',
                      });
                    }
                  } catch (error: any) {
                    retryErrors.push({
                      model: model.schemaTitle || model.title,
                      error: error?.message || 'Unknown error',
                    });
                  }
                }
                
                // Remove retry message
                setChatMessages((prevMessages) =>
                  prevMessages.filter((msg) => msg._id !== retryMessageId)
                );
                
                // Update success message
                if (retryCreatedModels.length > 0) {
                  const updatedSuccessText = retryCreatedModels.length === failedModels.length
                    ? `Successfully created ${retryCreatedModels.length} model${retryCreatedModels.length > 1 ? 's' : ''} on retry!`
                    : `Successfully created ${retryCreatedModels.length} of ${failedModels.length} model${failedModels.length > 1 ? 's' : ''} on retry.`;
                  
                  setChatMessages((prevMessages) =>
                    prevMessages.map((msg) => {
                      if (msg._id === successMessageId) {
                        return {
                          ...msg,
                          text: updatedSuccessText,
                          hasErrors: retryErrors.length > 0,
                          failedModels: retryErrors,
                          canRetry: retryErrors.length > 0,
                        };
                      }
                      return msg;
                    })
                  );
                } else {
                  setChatMessages((prevMessages) =>
                    prevMessages.map((msg) => {
                      if (msg._id === successMessageId) {
                        return {
                          ...msg,
                          text: successText + '\n\nRetry failed. You can continue without these models.',
                          canRetry: false,
                        };
                      }
                      return msg;
                    })
                  );
                }
              }
            } : undefined,
          },
        ])
      );
    } else if (errors.length > 0) {
      // All models failed
      const errorMessageId = uniqueId();
      const errorText = `Sorry, I couldn't create the selected models. You can try again or continue without them.`;
      const failedModelNames = errors.map(e => e.model).join(', ');
      
      setChatMessages((prevMessages) =>
        GiftedChat.append(prevMessages, [
          {
            _id: errorMessageId,
            text: `${errorText}\n\nFailed models: ${failedModelNames}`,
            createdAt: new Date(),
            system: true,
            received: true,
            canRetry: true,
            failedModels: errors,
            retryFunction: async () => {
              // Retry creating all models
              const retryMessageId = uniqueId();
              setChatMessages((prevMessages) =>
                GiftedChat.append(prevMessages, [
                  {
                    _id: retryMessageId,
                    text: `Retrying creation of ${selectedModels.length} model${selectedModels.length > 1 ? 's' : ''}...`,
                    createdAt: new Date(),
                    system: true,
                    received: true,
                  },
                ])
              );
              
              // Retry logic (same as above)
              const retryCreatedModels = [];
              const retryErrors = [];
              
              for (const model of selectedModels) {
                try {
                  const modelData = {
                    dataTitle: model.schemaTitle || model.title || 'Model',
                    dataTags: model.dataTags || [],
                    dataSchema: model.schemaContent || model.schema || [],
                    description: model.description || '',
                    dataContent: null,
                  };
                  
                  const saveResult = await apis.saveData({
                    id: userId,
                    data: modelData,
                  });
                  
                  if (saveResult?.success) {
                    retryCreatedModels.push(model);
                  } else {
                    retryErrors.push({
                      model: model.schemaTitle || model.title,
                      error: saveResult?.message || 'Failed to create model',
                    });
                  }
                } catch (error: any) {
                  retryErrors.push({
                    model: model.schemaTitle || model.title,
                    error: error?.message || 'Unknown error',
                  });
                }
              }
              
              // Remove retry message
              setChatMessages((prevMessages) =>
                prevMessages.filter((msg) => msg._id !== retryMessageId)
              );
              
              // Update error message
              if (retryCreatedModels.length > 0) {
                setChatMessages((prevMessages) =>
                  prevMessages.map((msg) => {
                    if (msg._id === errorMessageId) {
                      return {
                        ...msg,
                        text: `Successfully created ${retryCreatedModels.length} of ${selectedModels.length} model${selectedModels.length > 1 ? 's' : ''} on retry!`,
                        canRetry: retryErrors.length > 0,
                        failedModels: retryErrors,
                      };
                    }
                    return msg;
                  })
                );
              } else {
                setChatMessages((prevMessages) =>
                  prevMessages.map((msg) => {
                    if (msg._id === errorMessageId) {
                      return {
                        ...msg,
                        text: errorText + '\n\nRetry failed. You can continue without these models.',
                        canRetry: false,
                      };
                    }
                    return msg;
                  })
                );
              }
            },
          },
        ])
      );
    }

    // Proceed to integration suggestions (even if some models failed)
    setTimeout(async () => {
      setOnboardingStage(null);
      await fetchIntegrationSuggestions();
    }, 1000);
  } catch (error) {
    console.error('❌ [EasyModeScreen] Error in model selection:', error);
    const errorMessageId = uniqueId();
    setChatMessages((prevMessages) =>
      GiftedChat.append(prevMessages, [
        {
          _id: errorMessageId,
          text: 'Sorry, there was an error creating the models. You can continue without them.',
          createdAt: new Date(),
          system: true,
          received: true,
          canRetry: true,
          retryFunction: async () => {
            // Retry the entire model selection process
            await handleModelSelection(selectedModels, config);
          },
        },
      ])
    );

    // Still proceed to integrations even if model creation fails
    setTimeout(async () => {
      setOnboardingStage(null);
      await fetchIntegrationSuggestions();
    }, 1000);
  }
};

/**
 * Handle skipping model selection
 */
export const handleModelSelectionSkip = async (config: ModelHandlersConfig) => {
  const {
    dispatch,
    setChatMessages,
    setOnboardingStage,
    fetchIntegrationSuggestions,
  } = config;

  const skipMessageId = uniqueId();
  setChatMessages((prevMessages) =>
    GiftedChat.append(prevMessages, [
      {
        _id: skipMessageId,
        text: 'No problem! You can create models later. Let\'s continue with integration suggestions.',
        createdAt: new Date(),
        system: true,
        received: true,
      },
    ])
  );

  setTimeout(async () => {
    setOnboardingStage(null);
    await fetchIntegrationSuggestions();
  }, 500);
};

